import type { RefObject, ChangeEvent } from 'react';
import Media from 'react-media';
import {
  Badge,
  breakpoints,
  Inline,
  RadioCard,
  RadioGroup,
  Stack,
  Text,
  textColors,
  textVariants,
  vars,
} from '@etg/wings';
import { CheckCircle } from '@phosphor-icons/react';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { Field as ReduxFormField } from 'redux-form';
import type { BoundNew } from '@eti/schema-types';
import { Tier } from '@eti/schema-types';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { FAST_TRACK, LOUNGE } from '@eti/products';
import { price as priceUtils } from '@eti/utils';
import { mobileBundleOptionOrder } from '../../../../utils/transformBundleData';
import type {
  AncillaryBundleOptionType,
  AncillaryBundleOptionsFeatureType,
  FormatType,
  FormatValueType,
} from '../../../../common/utils/models';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import type { AvailableExtraProductsType } from '../v3/branded-fares/model';
import { getAvailability } from './utils';

const { formatPrice, getPriceDivided } = priceUtils;

const cardStyles = css`
  background-color: ${etiColors.grayScale[4]};
  border: none;
`;

const promotionalBadgeStyles = css`
  left: 50%;
  position: absolute;
  top: -16px;
  transform: translateX(-50%);
  white-space: nowrap;
  width: fit-content;
`;

const priceInformationStyles = css`
  border-bottom: 1px solid ${vars.colors.divider};
  display: flex;
  flex-direction: column;
  padding: 16px 0;
`;

const includedProductStyles = css`
  padding-top: 16px;
`;

const checkCircleStyles = css`
  flex-shrink: 0;
  margin-top: 2px;
`;

const errorCardStyles = css`
  border: solid ${vars.colors.signal.critical.foreground} 1px;
`;

const lineStrikeStyle = css`
  text-decoration: line-through;
`;

const availabilityProductList = [FAST_TRACK.id.toString(), LOUNGE.id.toString()];

const texts = {
  etgFaresTitle: 'Product.ETG.Fares.Title',
  piecesBaggageText: 'Product.BaggagePerTraveler.Pieces',
  promotionalText: 'Product.ETG.Fares.Promotional.Text',
  screenReaderIncludedProductsText: 'ScreenReader.Product.ETG.Fares.Included.Text',
  selectOneOptionText: 'Product.SelectOne.Label',
  weightValuesBaggageText: 'Product.BaggagePerTraveler.Weight',
};

interface EtgFaresProps {
  availableExtraProducts: AvailableExtraProductsType[];
  bounds: BoundNew[];
  discountPercentage: number;
  firstRadioRef: RefObject<HTMLInputElement>;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>) => void;
  hasValidationError: boolean;
  input?: { value: string };
  name: string;
  numberOfTravelersWithoutInfants: number;
  sellableOptions: AncillaryBundleOptionType[];
}

const EtgFaresRadioGroup = ({
  availableExtraProducts,
  bounds,
  discountPercentage,
  firstRadioRef,
  handleOnChange,
  hasValidationError,
  input,
  name,
  numberOfTravelersWithoutInfants,
  sellableOptions,
}: EtgFaresProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const pricePerTravelerAndBound = p('ETG.Fares.Price.Per.Person.And.Bound.Enabled');
  const pricePerTraveler = p('Bundle.Overlay.Price.Per.Person.Enabled');
  const totalPriceText = t('Product.FlexibleTicket.Bundle.Price.Text');
  const pricePerTravelerText = t('Price.Per.Person');
  const pricePerTravelerAndBoundText = t('Product.ETG.Fares.Price.Per.Person.And.Bound.Text');
  const whenAvailableText = t('Product.ETG.Fares.PartialAvailable.Information.Text');
  const { brand } = useSiteContext();

  const formattedPrice = (price: number) => {
    if (pricePerTravelerAndBound && bounds.length > 1) {
      const pricePerBound = price / bounds.length;
      return formatPrice(getPriceDivided(pricePerBound, numberOfTravelersWithoutInfants));
    }
    if (pricePerTraveler) {
      return formatPrice(getPriceDivided(price, numberOfTravelersWithoutInfants));
    }

    return formatPrice(price);
  };

  const priceText = () => {
    if (pricePerTravelerAndBound && bounds.length > 1) {
      return pricePerTravelerAndBoundText;
    }
    if (pricePerTraveler) {
      return pricePerTravelerText;
    }

    return totalPriceText;
  };

  const additionalProductInformation = (product: AncillaryBundleOptionsFeatureType) => {
    if (availabilityProductList.includes(product.productTypeId)) {
      const availabilityList =
        getAvailability(availableExtraProducts, bounds, product.productTypeId) ?? [];
      const hasUnavailable = availabilityList.some((airport) => !airport.available);

      return hasUnavailable && whenAvailableText ? `(${whenAvailableText})` : null;
    }

    const { pieces, weight, weightUnit } = product;
    if (!(pieces && weight && weightUnit)) {
      return null;
    }

    const { piecesBaggageText, weightValuesBaggageText } = texts;
    return `${t(piecesBaggageText, pieces)} ${t(weightValuesBaggageText, weight, weightUnit)}`;
  };

  const orderSellableOption = (isDesktop: boolean) => {
    if (isDesktop) {
      return sellableOptions;
    }

    return mobileBundleOptionOrder(sellableOptions);
  };

  return (
    <Stack>
      <RadioGroup
        className={cardStyles}
        name={name}
        onChange={handleOnChange}
        selectedValue={input?.value}
      >
        <RadioGroup.Legend className={screenReadersOnlyStyles}>
          {`${t(texts.etgFaresTitle, discountPercentage, brand.code)} ${t(
            texts.selectOneOptionText,
          )} ${t('Validation.Required')}`}
        </RadioGroup.Legend>

        <Media query={mediaQueries.medium.up}>
          {(isDesktop) => (
            <Inline collapseBelow={breakpoints._768} noWrap>
              {orderSellableOption(isDesktop).map((option, optionIndex) => (
                <RadioCard
                  aria-checked={Boolean(input?.value)}
                  aria-describedby={`promotional-${optionIndex}-text option-${optionIndex}-price-content included-products-content-${optionIndex}`}
                  className={hasValidationError ? errorCardStyles : ''}
                  id={`choice.${name}.${option.id}`}
                  key={option.id}
                  label={
                    <Text
                      data-testid={`radiocard-label-${option.title}`}
                      variant={textVariants._18_EMPHASIZED}
                    >
                      {option.title}
                    </Text>
                  }
                  ref={optionIndex === 0 ? firstRadioRef : null}
                  value={option.id}
                >
                  {option.type === Tier.Recommended && (
                    <Badge
                      className={promotionalBadgeStyles}
                      id={`promotional-${optionIndex}-text`}
                      variant="promotional"
                    >
                      {t(texts.promotionalText)}
                    </Badge>
                  )}
                  <Stack
                    className={priceInformationStyles}
                    id={`option-${optionIndex}-price-content`}
                    spacing={4}
                  >
                    <Text as="p" className={lineStrikeStyle} color={textColors.SECONDARY}>
                      <span className={screenReadersOnlyStyles}>
                        {`${t('ScreenReader.Product.ETG.Fares.Original.Price.Text')}:
                        ${priceUtils.replaceWithCurrencyName(
                          formattedPrice(option.originalPrice),
                        )}`}
                      </span>
                      <span aria-hidden="true">+{formattedPrice(option.originalPrice)}</span>
                    </Text>
                    <Text as="p" variant={textVariants._24_EMPHASIZED}>
                      <span className={screenReadersOnlyStyles}>
                        {`${t('ScreenReader.Product.ETG.Fares.Discounted.Price.Text')}:
                        ${priceUtils.replaceWithCurrencyName(formattedPrice(option.priceRaw))}`}
                      </span>
                      <span aria-hidden="true">+{formattedPrice(option.priceRaw)}</span>
                    </Text>
                    <Text>{priceText()}</Text>
                  </Stack>
                  <Stack
                    className={includedProductStyles}
                    id={`included-products-content-${optionIndex}`}
                  >
                    <Text className={screenReadersOnlyStyles}>
                      {`${option.features.length} ${t(texts.screenReaderIncludedProductsText)}:`}
                    </Text>
                    <Stack as="ul" spacing={4}>
                      {option.features.map((product, index) => (
                        <Inline as="li" key={`${option.title}-feature-${index}`} noWrap spacing={8}>
                          <CheckCircle
                            aria-hidden
                            className={checkCircleStyles}
                            color={vars.colors.signal.positive.foreground}
                            size={16}
                            weight="fill"
                          />
                          <Text as="bdi">
                            {product.name} {additionalProductInformation(product)}
                          </Text>
                        </Inline>
                      ))}
                    </Stack>
                  </Stack>
                </RadioCard>
              ))}
            </Inline>
          )}
        </Media>
      </RadioGroup>
    </Stack>
  );
};

const hasBeenSelected = (value?: FormatValueType) => value !== null && value !== undefined;

const EtgFaresSelection = ({ name, ...props }: EtgFaresProps) => {
  const format = (value?: FormatType) =>
    hasBeenSelected(value?.value) ? value?.value.id : undefined;

  return (
    <ReduxFormField
      component={EtgFaresRadioGroup as any}
      format={format}
      name={name}
      props={{ name, ...props } as any}
    />
  );
};

export default EtgFaresSelection;
